import { postJson } from "./http";
import type { ZohoLedgerDTO, ZohoTaxDTO } from "./zohoRecordsApi";

/**
 * TDS and TCS rates — the tds-tcs-review screen's section catalog (read live
 * from Zoho) and its writes into a client's real Zoho Books: adding a rate,
 * editing one, and switching one on or off.
 */

/**
 * One section a TDS or TCS rate can cite, as Zoho enumerates them.
 *
 * `value` is what goes in `tax_specific_type` and is the only part Zoho
 * matches on. `section_no` is what Zoho's own rates list shows in its Tax Type
 * column. `old_section_no` is the Income Tax Act 1961 number the Act 2025
 * section replaced, and is empty on the 1961 list itself.
 */
export interface TaxSectionDTO {
  value: string;
  description: string;
  section_no: string;
  old_section_no: string;
}

/** Keyed by Income Tax Act version: "1" is 1961, "2" is 2025. */
export type TaxSectionsByVersion = Record<string, TaxSectionDTO[]>;

interface SectionsEnvelope {
  success: boolean;
  data: { sections: TaxSectionsByVersion };
}

/**
 * The sections Zoho will accept, for this client's organisation.
 *
 * Read live rather than shipped in the app. The vocabulary is closed, Zoho
 * refuses anything outside it, and the Income Tax Act 2025 renamed every
 * section — so a list held here would be right for one Act and wrong for the
 * other. Both versions come back together because the form lets a CA switch
 * between them.
 *
 * Reads only. Nothing is written to the books by this call.
 */
export const fetchTaxSections = async (
  publicId: string,
  taxType: "tds" | "tcs" = "tds"
): Promise<TaxSectionsByVersion> =>
  (await postJson<SectionsEnvelope>("/api/onboarding/taxes/sections", {
    public_id: publicId,
    tax_type: taxType,
  })).data.sections;

/**
 * What an add-rate form collects. Ledgers and the end date are optional.
 *
 * The two ledger fields are the only part that differs between the taxes, and
 * they differ in a way worth reading twice: `tax_account_id` is the RECEIVABLE
 * on a TDS rate and the PAYABLE on a TCS one. Whichever tax is being sent, only
 * that tax's two fields belong in the payload.
 */
export interface NewDirectTaxRate {
  tax_name: string;
  tax_percentage: string;
  tax_specific_type: string;
  /** 1 = Income Tax Act 1961, 2 = Income Tax Act 2025. */
  version: 1 | 2;
  start_date: string;
  end_date?: string;
  /** TDS payable. Other Current Liability. */
  tds_payable_account_id?: string;
  /** TDS receivable, or TCS payable. See the note above. */
  tax_account_id?: string;
  /** TCS receivable. Other Current Asset. */
  tcs_receivable_account_id?: string;
}

export interface CreatedRateDTO {
  created: { tax_id: string; tax_name: string };
  /**
   * Set when the rate was made but the re-read that follows it failed. The rate
   * exists; the list beside it may be a moment behind.
   */
  reread_error: string | null;
  /** That one tax's whole list again, re-read from Zoho after the create. */
  rates: ZohoTaxDTO[];
  /**
   * The chart of accounts again, because a create can add to it: Zoho makes the
   * TCS Payable and TCS Receivable accounts the first time an organisation gets
   * a TCS rate. Without these the screen would name accounts it does not hold.
   */
  ledgers: ZohoLedgerDTO[];
}

interface CreatedRateEnvelope {
  success: boolean;
  data: CreatedRateDTO;
}

/**
 * Add a TDS or TCS rate to the client's real Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS. There is no undo here worth the name: a
 * rate that has been used on a bill cannot be deleted, only ended. Do not call
 * it speculatively or retry it blind; a failure that reached Zoho may still
 * have created the rate.
 *
 * The readable refusal lives under `errors.rate` — "This client already has a
 * 2% rate on that section…", or Zoho's own refusal passed through.
 */
export const createDirectTaxRate = async (
  publicId: string,
  kind: "tds" | "tcs",
  rate: NewDirectTaxRate
): Promise<CreatedRateDTO> =>
  (
    await postJson<CreatedRateEnvelope>(
      `/api/onboarding/taxes/${kind}/create`,
      { public_id: publicId, ...rate },
      ["rate"]
    )
  ).data;

/**
 * What an edit-rate form collects: everything a create sends, plus the id of the
 * rate being changed, and with the end date no longer optional.
 *
 * `end_date` ALWAYS TRAVELS, "" included. Zoho's rate update is a full replace —
 * proven live on 2026-08-14 by capturing what the Books web app itself sends
 * when a rate is saved, which is the whole rate every time — so a field left out
 * of the payload is not "unchanged", it is whatever Zoho decides in its absence.
 * A rate that was ended on 31 March and is being reopened has to say so with an
 * empty string; omitting the field would leave the old end date in place.
 *
 * The ledger fields keep their create-time meaning, inversion and all:
 * `tax_account_id` is the RECEIVABLE on a TDS rate and the PAYABLE on a TCS one.
 */
export interface EditedDirectTaxRate {
  tax_id: string;
  tax_name: string;
  tax_percentage: string;
  tax_specific_type: string;
  /** 1 = Income Tax Act 1961, 2 = Income Tax Act 2025. */
  version: 1 | 2;
  start_date: string;
  /** Always sent. "" means open-ended. See the note above. */
  end_date: string;
  /** TDS payable. Other Current Liability. */
  tds_payable_account_id?: string;
  /** TDS receivable, or TCS payable. See the note above. */
  tax_account_id?: string;
  /** TCS receivable. Other Current Asset. */
  tcs_receivable_account_id?: string;
}

export interface UpdatedRateDTO {
  updated: { tax_id: string; tax_name: string };
  /**
   * Set when the rate was changed but the re-read that follows it failed. The
   * change is in Zoho; the list beside it may be a moment behind.
   */
  reread_error: string | null;
  /** That one tax's whole list again, re-read from Zoho after the update. */
  rates: ZohoTaxDTO[];
  /** The chart of accounts again, for the same reason a create returns it. */
  ledgers: ZohoLedgerDTO[];
}

interface UpdatedRateEnvelope {
  success: boolean;
  data: UpdatedRateDTO;
}

/**
 * Change a TDS or TCS rate in the client's real Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS, and it overwrites: Zoho keeps no history of
 * what the rate said before, and every bill that cites the rate now reads with
 * the new name and the new percentage. Editing an in-use rate is how a client's
 * past bills change wording under them, which is why Zoho's own screen puts this
 * behind a row menu rather than making it the obvious thing to do.
 *
 * Only `tds` is routed today. The kind stays in the signature because the two
 * taxes differ in exactly one way — which payload field each ledger goes in —
 * and that is already settled by `TAX_LEDGERS`.
 *
 * The readable refusal lives under `errors.rate`, the same key create uses:
 * either our own duplicate sentence (which excludes the rate being edited) or
 * Zoho's own words passed through.
 */
export const updateDirectTaxRate = async (
  publicId: string,
  kind: "tds" | "tcs",
  rate: EditedDirectTaxRate
): Promise<UpdatedRateDTO> =>
  (
    await postJson<UpdatedRateEnvelope>(
      `/api/onboarding/taxes/${kind}/update`,
      { public_id: publicId, ...rate },
      ["rate"]
    )
  ).data;

export interface CreatedLedgerDTO {
  /**
   * `account_id` can come back "" on the rare create where Zoho made the
   * account but did not return its id — the account exists either way, and
   * `reread_error` carries the sentence saying what to do about it.
   */
  account: { account_id: string; account_name: string; account_type: string };
  /** Set when the account was made but the chart re-read after it failed. */
  reread_error: string | null;
  /** The chart of accounts again, now carrying the new account. */
  ledgers: ZohoLedgerDTO[];
}

interface CreatedLedgerEnvelope {
  success: boolean;
  data: CreatedLedgerDTO;
}

/**
 * Add one account to the client's real chart of accounts — the rate form's
 * "+ New account", for a payable or receivable the dropdown does not offer.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS. Gentler than a rate, because Zoho keeps
 * account names unique and refuses a repeat rather than making a twin — but an
 * account nobody meant to make still sits in the client's chart until somebody
 * deletes it there.
 *
 * Only the two types a rate can point at are accepted: `other_current_liability`
 * (the payable) and `other_current_asset` (the receivable). The readable
 * refusal lives under `errors.account`.
 */
export const createLedgerAccount = async (
  publicId: string,
  account: { account_name: string; account_type: string }
): Promise<CreatedLedgerDTO> =>
  (
    await postJson<CreatedLedgerEnvelope>(
      "/api/onboarding/taxes/ledgers/create",
      { public_id: publicId, ...account },
      ["account"]
    )
  ).data;

export interface RateStatusDTO {
  /** That one tax's whole list again, re-read from Zoho after the switch. */
  rates: ZohoTaxDTO[];
  /** Set when the switch went through but the re-read after it did not. */
  reread_error: string | null;
}

interface RateStatusEnvelope {
  success: boolean;
  data: RateStatusDTO;
}

/**
 * Switch a rate on or off in the client's real Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS, but it is the one write on this screen that
 * undoes itself: the opposite call puts the rate back exactly as it was, which
 * is why neither this nor Zoho's own menu asks for a confirmation.
 *
 * Off is not the same as ended. An inactive rate keeps its dates and simply
 * stops being offered on new transactions; Zoho leaves it out of the dropdown
 * even for a date it covers. Nothing already posted with it changes.
 */
export const setDirectTaxRateStatus = async (
  publicId: string,
  kind: "tds" | "tcs",
  taxId: string,
  active: boolean
): Promise<RateStatusDTO> =>
  (
    await postJson<RateStatusEnvelope>(
      `/api/onboarding/taxes/${kind}/status`,
      { public_id: publicId, tax_id: taxId, active },
      ["rate"]
    )
  ).data;
